import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router';
import { initAnalyticsSession } from '@gonfalon/analytics/initAnalyticsSession';
import { initCanaryToken } from '@gonfalon/analytics/initCanaryToken';
import { initSegmentAndCandu } from '@gonfalon/analytics/initSegmentAndCandu';
import { subscribeToRouteViews } from '@gonfalon/analytics/subscribeToRouteViews';
import { enableLanguagePicker } from '@gonfalon/dogfood-flags/dogfood-flags';
import { onDOMReady } from '@gonfalon/dom/onDOMReady';
import { initializeFrontendVersionPersistence } from '@gonfalon/frontend-version/initialize';
import { LocaleProvider } from '@gonfalon/messages/LocaleProvider';
// The only modules allowed to consume the provider are application entry points.
import { initializeQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { ReactQueryClientProvider } from '@gonfalon/react-query-client/ReactQueryClientProvider';
import { initializeAPIClient } from '@gonfalon/rest-api/client';
import { trackPageLoadCustomMetrics } from '@gonfalon/telemetry/trackPageLoadCustomMetrics';
import { ThemeProvider } from '@gonfalon/theme/ThemeProvider';
import thunk from 'redux-thunk';

import { IconProvider } from '#components/IconProvider';
import { createStore } from '#reducers/createStore';

import { unauthenticatedRoutes } from '#routes/unauthenticatedRoutes';

export const load = () => {
  initializeQueryClient();

  const { store } = createStore({
    middlewares: [thunk],
  });

  initAnalyticsSession();
  trackPageLoadCustomMetrics();
  initCanaryToken();
  initSegmentAndCandu();

  initializeFrontendVersionPersistence();
  initializeAPIClient();

  onDOMReady(() => {
    const selector = '.ld-container';
    const container: HTMLElement | null = document.querySelector(selector);

    if (!container) {
      return;
    }

    const root = createRoot(container);
    const router = createBrowserRouter(createRoutesFromElements(unauthenticatedRoutes()));
    subscribeToRouteViews(router);

    root.render(
      <ReactQueryClientProvider>
        <Provider store={store}>
          <IconProvider>
            <ThemeProvider>
              <LocaleProvider canSelectLocale={enableLanguagePicker()}>
                <RouterProvider router={router} />
              </LocaleProvider>
            </ThemeProvider>
          </IconProvider>
        </Provider>
      </ReactQueryClientProvider>,
    );
  });
};
